import $ from 'jquery';

import GlobalEvents from 'app/GlobalEvents';
import BasePaymentController from 'app/src/controllers/payments/BasePaymentController';
import PaymentServiceAlert from 'app/src/controllers/payments/PaymentServiceAlert';
import PaymentTotalViewController from 'app/src/controllers/payments/PaymentTotalViewController';
import PaymentViewController from 'app/src/controllers/payments/PaymentViewController';
import Format from 'app/src/data/Format';
import CreditAccount from 'app/src/data/models/CreditAccount';
import PaymentTransaction from 'app/src/data/models/PaymentTransaction';
import PaymentType from 'app/src/data/models/PaymentType';
import Localize from 'app/src/localize/Localize';
import PaymentFactory from 'app/src/payments/PaymentFactory';
import PaymentOperations from 'app/src/payments/PaymentOperations';
import PaymentTypeManager from 'app/src/payments/PaymentTypeManager';
import SaleCalculationHelper from 'app/src/payments/SaleCalculationHelper';
import { createReactRenderer } from 'helpers/react-renderer';
import AddDepositModal from 'payments/components/AddDepositModal';
import AddPaymentDeposit from 'payments/PaymentDepositModal/index';
import { hasFeature } from 'root/api/admin/features';
import { renderDepositPaymentModal } from 'root/apps/QuoteMachineIntegration/components/DepositPaymentModal';
import { renderInvoicePaymentModal } from 'root/apps/QuoteMachineIntegration/components/InvoicePaymentModal';
import { renderInvoicingModal } from 'root/apps/QuoteMachineIntegration/components/InvoicingModal';
import {
  PaymentTileType,
  renderPaymentTile,
} from 'root/apps/QuoteMachineIntegration/components/PaymentTile';
import {
  LsInvoicingInvoiceCreationTime,
  LsInvoicingInvoiceFinalizationTime,
} from 'root/apps/QuoteMachineIntegration/datadogEventConstants';
import InvoiceService from 'root/apps/QuoteMachineIntegration/services/InvoiceService';
import CreditAccountService, {
  buildCreditAccountCreatePayload,
} from 'root/apps/QuoteMachineIntegration/services/v1/CreditAccountService';
import DepositUsageIntentService from 'root/apps/QuoteMachineIntegration/services/v1/DepositUsageIntentService';
import InvoicePaymentInstructionService from 'root/apps/QuoteMachineIntegration/services/v1/InvoicePaymentInstructionService';
import RefundIntentService, {
  buildCustomerFromData,
  buildEmployeeFromData,
  buildLocationFromShopData,
} from 'root/apps/QuoteMachineIntegration/services/v1/RefundIntentService';
import { logInvoiceCreationMetric } from 'root/apps/QuoteMachineIntegration/utils/datadogLogging';
import {
  getDepositPaymentsPayload,
  getInvoicePaymentsPayload,
  getSalePaymentsPayload,
} from 'root/apps/QuoteMachineIntegration/utils/InvoicingPayload';
import {
  GetPendingCreditAccountPaymentAmount,
  getProcessedPaymentsFromSale,
  getSalePaymentsForInvoice,
} from 'root/apps/QuoteMachineIntegration/utils/Payment';
import { getProposalPreviewURL } from 'root/apps/QuoteMachineIntegration/utils/ProposalPreviewConfig';
import { getSaleLineCount } from 'root/apps/QuoteMachineIntegration/utils/saleHelpers';
import { datadogRum } from 'root/datadog/datadogRum';
import Modules from 'shared_modules/Modules';
import Monitoring from 'shared_modules/Monitoring';

// These must match the values indicated by the webPOS legacy UI.  If we subsume responsibility
// for the deposits UI in the future then this is no longer necessary.
const _DepositPaymentTypes = {
  CASH: 'cash_payment',
  CREDIT_CARD: 'credit_card',
  GIFT_CARD: 'gift_card',
  USER_DEFINED: 'payment_type_',
};

// Here are listed the unsupported bank card types
// for the refund deposit. Other unsupported card type
// should be listed here. This piece of logic should be
// replaced in the future by a multi-dimensional state machine
// based on values such as the card type, the payment type, the processor, etc. to avoid
// having to keep this array up to date manually.
// The same list is present in withdraw.php
const NOT_SUPPORTED_DEPOSIT_REFUND_CARD_TYPE = [PaymentTransaction.SupportedCardType.INTERAC];

export default class PaymentController extends BasePaymentController {
  constructor(saleManager, paymentProvider) {
    super(saleManager);
    this._paymentProvider = paymentProvider;

    if (Modules.hasModule('customers.invoicing_integration.free_tier')) {
      if (this._saleManager.totalInvoicePaymentAmount && !this.invoicesToPayTile) {
        this.addInvoicesToPayTile();
      }

      if (this._saleManager.deposits.hasDeposits && !this.depositToPayTile) {
        this.addDepositToPayTile();
      }
    }

    this.listenTo(
      this._globalChannel.vent,
      GlobalEvents.Events.register.ADD_DEPOSIT,
      this.onAddDeposit
    );
    this.listenTo(
      this._globalChannel.vent,
      GlobalEvents.Events.register.ADD_DEPOSIT_REFUND,
      this.onDepositRefund
    );
    this.listenTo(
      this._globalChannel.vent,
      GlobalEvents.Events.register.ADD_INVOICE_PAYMENT,
      this.onAddInvoicePayment
    );
    this.listenTo(
      this._globalChannel.vent,
      GlobalEvents.Events.register.ADD_DEPOSIT_PAYMENT,
      this.onAddDepositPayment
    );
    this.listenTo(
      this._globalChannel.vent,
      GlobalEvents.Events.register.ADD_INVOICING_DEPOSIT,
      this.onAddInvoicingDeposit
    );
    this.listenTo(
      this._globalChannel.vent,
      GlobalEvents.Events.register.PREVIEW_INVOICE,
      this.onPreviewInvoice
    );
  }

  /**
   * Custom cleanup code when the PaymentController is dismissed.
   */
  destroy() {
    // need to cleanup the payment view controllers when closing
    this.closePayments();

    super.destroy();
  }

  /**
   *
   * @param {string} amount   The amount to refund for a deposit
   * @param {object} options  An options hash to provide details about the deposit
   * @param {number} options.paymentTypeID    The payment type ID of the deposit refund
   * @param {number} options.originalPaymentID   The sale payment ID of an original payment; used for
   *                                                  credit card refunds
   */
  async onDepositRefund(
    amount,
    options = {
      cardNumber: null,
      cardType: null,
      paymentTypeID: 0,
      originalPaymentID: 0,
      paymentID: null,
      isRemote: false,
    }
  ) {
    // This flag checks that the specified card type requires
    // cardholder interaction in order to properly
    // refund the deposit. In the case of the deposit
    // refund any card type in NOT_SUPPORTED_DEPOSIT_REFUND_CARD_TYPE
    // will likely throw an error message to the user that the card type
    // is not supported.
    const isCardTypeNotSupportedForDepositRefund =
      options && NOT_SUPPORTED_DEPOSIT_REFUND_CARD_TYPE.includes(options.cardType);
    const paymentID = options && options.paymentID;
    // deposit refunds are expressed as negative deposits
    let paymentAmount = Format.parseDecimal(amount, 0).abs().negated();
    // This is a deposit refund for credit card payment so obtain the credit card payment type ID
    let paymentTypeID = options.paymentTypeID
      ? options.paymentTypeID
      : PaymentTypeManager.getPaymentIdForType(PaymentTypeManager.Types.TERMINAL);

    if (!paymentAmount.isZero() && paymentTypeID) {
      // the data coming from the legacy code is different to identify the type of payment
      // when doing deposits vs refund deposits.  Normalize to a payment type.
      let paymentType = PaymentTypeManager.getPaymentType(paymentTypeID, true);
      let deposit = this._createDeposit(paymentAmount, paymentType, true);

      if (deposit) {
        if (
          (paymentType && paymentType.get('type') === PaymentType.Types.CREDIT_CARD) ||
          (paymentType && paymentType.get('code') === PaymentType.Codes.CREDIT_CARD)
        ) {
          deposit.depositPayment.model.set({
            refPaymentID: options.originalPaymentID,
            saleID: this._saleManager.sale.id,
          });

          if (paymentID) {
            deposit.depositPayment.model.set({
              paymentID,
            });
          }

          if (options.cardNumber) {
            deposit.depositPayment.model.cardNumber = options.cardNumber;
          }
        }

        if (options.isRemote) {
          deposit.depositPayment.model.set('isRemote', true);
        }

        if (Modules.hasModule('invoicing.api.v1.refund_intent')) {
          const refundIntentSuccess = await this._upsertRefundIntent(
            Format.parseDecimal(amount, 0)
          );
          if (!refundIntentSuccess) {
            alertUser(Localize.translate('Could not process refund intent. Please try again.'));
            return;
          }
        }

        if (
          deposit.depositPayment.isServicePayment() ||
          (!this._paymentProvider.activeGatewaySupportsRefundByReference &&
            (paymentType.get('type') === PaymentType.Types.CREDIT_CARD ||
              paymentType.get('code') === PaymentType.Codes.CREDIT_CARD) &&
            this._paymentProvider.activeGateway)
        ) {
          if (
            this._paymentProvider.isPlatformV3() &&
            (await hasFeature('PaymentsDepositInteracRefunds'))
          ) {
            PaymentOperations.doLSPAYRefundDepositOperation(
              deposit,
              this._saleManager,
              this._paymentProvider,
              options.cardType
            );
          }

          // This code should not be reachable since we disabled the selection
          // of the unsupported card types in the backend directly, but it does add
          // a second level of protection if someone manages to bypass
          // the disabled select option.
          else if (isCardTypeNotSupportedForDepositRefund) {
            alertUser(
              Localize.translate(
                'Refunding a deposit on this type of card is not supported. Please refund the customer by cash.'
              )
            );
          } else {
            PaymentOperations.doRefundDepositOperation(
              deposit,
              this._saleManager,
              this._paymentProvider
            );
          }
        } else {
          // Add deposit refund to local state
          if (!this._saleManager.deposits.addDepositRefund(deposit)) {
            alertUser(Localize.translate('Could not add the refund.'));
            return;
          }

          // Success
          flashNotifier(
            'success',
            Localize.translate(`Deposit Refund Of <var>${Format.formatMoney(amount)}</var> Added.`),
            4
          );
        }
      }
    }
  }

  onAddInvoicePayment() {
    if (!Modules.hasModule('customers.invoicing_integration.free_tier')) {
      return;
    }
    renderInvoicePaymentModal({
      transactionId: this._saleManager.sale.id,
      defaultValue: this._saleManager.invoicePaymentOptions,
      onCancel: () => {},
      onSubmit: async (options) => {
        const invoicePaymentOptionsFromMFE = options?.invoicesToPay?.filter(
          ({ invoiceId, amount }) => invoiceId && amount
        );

        if (
          Modules.hasModule('invoicing.api.v1.payment_instructions') &&
          invoicePaymentOptionsFromMFE &&
          invoicePaymentOptionsFromMFE.length > 0
        ) {
          const invoiceAllocations = invoicePaymentOptionsFromMFE.map((invoice) => ({
            invoiceId: invoice.invoiceId,
            amount: invoice.amount,
          }));

          try {
            await InvoicePaymentInstructionService.upsertInvoicePaymentInstruction({
              creditAccountId: this._saleManager.creditAccountID,
              saleId: this._saleManager.sale.id,
              invoiceAllocations,
              customer: this._saleManager.customer?.attributes,
              currency: window.merchantos?.locale?.currency_code || 'USD',
            });
          } catch (error) {
            datadogRum.addError(error, {
              creditAccountId: this._saleManager.creditAccountID,
              saleId: this._saleManager.sale.id,
              invoiceAllocations,
              message: 'Failed to create invoice payment instruction',
            });
            window.showNotifier('warning', 'Failed to save invoice payment instruction');
            return;
          }
        }

        this._saleManager.invoicePaymentOptions = {
          ...options,
          invoicesToPay: invoicePaymentOptionsFromMFE,
        };
        if (this._saleManager.totalInvoicePaymentAmount) {
          this.addInvoicesToPayTile();
        }
      },
      onError: (message) => {
        window.showNotifier('warning', message);
      },
    });
  }

  onAddDepositPayment() {
    if (!Modules.hasModule('customers.invoicing_integration.free_tier')) {
      return;
    }

    renderDepositPaymentModal({
      transactionId: this._saleManager.sale.id,
      balance: this._saleManager.pendingBalance.toNumber(),
      defaultValue: this._saleManager.depositPaymentOptions,
      onCancel: () => {},
      onSubmit: async (options) => {
        const depositPaymentOptionsFromMFE = options?.depositPayments?.filter(
          ({ creditMemoId, amount }) => creditMemoId && amount
        );
        const previousDepositPaymentOptions = this._saleManager.depositPaymentOptions;
        this._saleManager.depositPaymentOptions = {
          depositPayments: depositPaymentOptionsFromMFE,
        };

        // Upsert the deposit usage intent from the invoicing API
        if (
          Modules.hasModule('invoicing.api.v1.consume_deposit') &&
          depositPaymentOptionsFromMFE?.length
        ) {
          const totalAmount = depositPaymentOptionsFromMFE
            .reduce(
              (sum, { amount }) => sum.plus(Format.parseDecimal(amount, 0)),
              Format.parseDecimal(0)
            )
            .toNumber();

          try {
            await DepositUsageIntentService.upsertDepositUsageIntent({
              creditAccountId: this._saleManager.customer?.get('creditAccountID') || 0,
              customerId: `${this._saleManager.customer?.id}`,
              saleId: `${this._saleManager.sale.id}`,
              totalAmount,
              currency: window.merchantos.locale.currency_code,
              creditMemoAllocations: depositPaymentOptionsFromMFE.map(
                ({ creditMemoId, amount }) => ({
                  creditMemoId,
                  amount,
                })
              ),
              customer: this._saleManager.customer?.attributes,
            });
          } catch (error) {
            this._saleManager.depositPaymentOptions = previousDepositPaymentOptions;
            datadogRum.addError(error, {
              message: 'Failed to upsert deposit usage intent',
              saleId: `${this._saleManager.sale.id}`,
              creditAccountId: this._saleManager.customer?.get('creditAccountID'),
              totalAmount,
            });
            window.showNotifier(
              'warning',
              Localize.translate('Could not process deposit usage. Please try again.')
            );
          }
        }

        // Delete the deposit usage intent from the invoicing API
        if (
          Modules.hasModule('invoicing.api.v1.consume_deposit') &&
          (!depositPaymentOptionsFromMFE || depositPaymentOptionsFromMFE.length === 0)
        ) {
          try {
            await DepositUsageIntentService.deleteDepositUsageIntent(
              this._saleManager.customer?.get('creditAccountID') || 0,
              this._saleManager.sale.id
            );
          } catch (error) {
            this._saleManager.depositPaymentOptions = previousDepositPaymentOptions;
            datadogRum.addError(error, {
              message: 'Failed to delete deposit usage intent',
              saleId: this._saleManager.sale.id,
              creditAccountId: this._saleManager.customer?.get('creditAccountID'),
            });
            window.showNotifier(
              'warning',
              'Failed to delete deposit usage intent. Please try again.'
            );
          }
        }
      },
      onError: (message) => {
        window.showNotifier('warning', message);
      },
    });
  }

  /**
   * Handler when the add deposit event is triggered.
   */
  onAddDeposit(isAccountPayment) {
    // Show the deposit modal to retrieve the amount
    if (!this.addDepositModal) {
      this._saleManager.customer
        .getOpenOrders()
        .done(async (openOrders) => {
          let totalOpenOrders = Format.parseDecimal(0);
          if (openOrders && openOrders.find) {
            const allOpenOrderRecord = openOrders.find(
              (openOrderTotal) => openOrderTotal.type === 'all'
            );
            if (allOpenOrderRecord) {
              totalOpenOrders = Format.parseDecimal(allOpenOrderRecord.total, 0);
            }
          }
          await hasFeature('RegisterPaymentDepositByPaylink').then((result) => {
            if (result) {
              const RenderedComponent = createReactRenderer({
                component: AddPaymentDeposit,
                selector: '[data-react-root="ReactModal"]',
                props: {
                  onHide: () => {
                    delete this.addDepositModal;
                    RenderedComponent.unmount();
                  },
                  isAccountPayment,
                  openOrders,
                  saleManager: this._saleManager,
                },
              });
              this.addDepositModal = RenderedComponent.mount();
            } else {
              const RenderedComponent = createReactRenderer({
                component: AddDepositModal,
                selector: '[data-react-root="ReactModal"]',
                props: {
                  onAddDeposit: (amount) => {
                    this._doAddDeposit(amount, isAccountPayment);
                  },
                  onHide: () => {
                    delete this.addDepositModal;
                    RenderedComponent.unmount();
                  },
                  isAccountPayment,
                  currency: Localize.getCurrencySymbol(),
                  openOrders: totalOpenOrders,
                  saleManager: this._saleManager,
                },
              });
              this.addDepositModal = RenderedComponent.mount();
            }
          });
        })
        .fail(() => {
          PaymentServiceAlert.alert('Could not retrieve the open orders');
        });
    }
  }

  /**
   * Handler when the add deposit event is triggered from the invoicing account balance component.
   */
  onAddInvoicingDeposit() {
    if (!Modules.hasModule('customers.invoicing_integration.free_tier')) {
      return;
    }
    if (!this.addDepositModal) {
      window.showNotifier('working');
      this._saleManager.customer
        .getOpenOrders()
        .done((openOrders) => {
          const RenderedComponent = createReactRenderer({
            component: AddPaymentDeposit,
            selector: '[data-react-root="ReactModal"]',
            props: {
              onHide: () => {
                delete this.addDepositModal;
                RenderedComponent.unmount();
              },
              onSubmit: () => {
                this.addDepositToPayTile();
              },
              isAccountPayment: false,
              openOrders,
              saleManager: this._saleManager,
            },
          });
          this.addDepositModal = RenderedComponent.mount();
        })
        .fail(() => {
          PaymentServiceAlert.alert('Could not retrieve the open orders');
        });
    }
  }

  /**
   * Refresh sale data from R-Series to get latest customer and credit account information
   * @private
   * @throws {Error} If refresh fails
   */
  async _refreshAndValidateSale() {
    try {
      await this._saleManager.refreshSale();
    } catch (error) {
      throw new Error(`Failed to refresh sale data: ${error.message}`);
    }
  }

  /**
   * Extract and validate credit limit data for syncing to invoicing-api
   * @private
   * @returns {{shouldUpdate: boolean, creditLimitCents: number|null, creditAccountId: number}}
   * @throws {Error} If credit account exists but data is invalid
   */
  _extractCreditLimitData() {
    const creditAccountId = this._saleManager.creditAccountID;

    // No credit account - skip sync (customer doesn't use credit accounts)
    if (!creditAccountId) {
      return { shouldUpdate: false, creditLimitCents: null, creditAccountId };
    }

    // Only load credit account if customer has credit account ID
    const creditAccount = this._saleManager.customer?.getRelation('CreditAccount', CreditAccount);

    // Customer has credit account - validate data integrity (fail-fast)
    if (!creditAccount) {
      throw new Error(
        `Customer has credit account ID ${creditAccountId} but credit account data failed to load`
      );
    }

    // Valid credit account data - proceed with sync (even if credit limit is $0)
    // Note: If creditLimit is invalid, .times() will throw naturally
    return {
      shouldUpdate: true,
      creditLimitCents: Math.round(creditAccount.get('creditLimit').times(100).toNumber()),
      creditAccountId,
    };
  }

  /**
   * Check if a fetch response is a 404 error for credit account not found
   * @private
   * @param {Response} response - Fetch API response object (will be cloned to preserve body)
   * @returns {Promise<boolean>} True if this is a credit account 404 error
   */
  async _isCreditAccount404Error(response) {
    if (response.status !== 404) {
      return false;
    }

    // Clone response to avoid consuming the body (can only read once)
    try {
      const clonedResponse = response.clone();
      const errorData = await clonedResponse.json();
      // Check if error message specifically indicates credit account not found
      const message = errorData?.error?.message || errorData?.message || errorData?.error || '';
      const lowerMessage = message.toLowerCase();

      // Be specific: must mention "credit account" to avoid false positives
      return lowerMessage.includes('credit account');
    } catch (e) {
      // If we can't parse response, don't assume - return false
      // Let the error bubble up naturally for proper handling
      return false;
    }
  }

  /**
   * Build POST /credit-accounts payload from customer data
   * @private
   * @param {string} creditAccountId - Credit account ID (will be formatted with ls-retail_ prefix)
   * @param {number} creditLimitCents - Credit limit in cents
   * @returns {object} Payload for POST /v1/credit-accounts
   * @throws {Error} If required customer data is missing
   */
  _buildCreditAccountCreatePayload(creditAccountId, creditLimitCents) {
    const customer = this._saleManager.customer;

    if (!customer) {
      throw new Error('Customer data is required to create credit account');
    }

    // Extract customer data from Backbone model into plain object format
    // expected by the shared buildCreditAccountCreatePayload function
    // Note: ContactAddresses needs to be mapped to Contact.Addresses format
    const contact = customer.has('Contact') ? customer.get('Contact') : {};
    const contactAddresses = customer.has('ContactAddresses')
      ? customer.get('ContactAddresses')
      : { ContactAddress: [] };

    const customerData = {
      customerID: customer.id,
      firstName: customer.get('firstName'),
      lastName: customer.get('lastName'),
      company: customer.get('company'),
      title: customer.get('title'),
      Contact: {
        ...contact,
        Addresses: contactAddresses,
      },
    };

    const currency = window.merchantos?.locale?.currency_code || 'USD';

    return buildCreditAccountCreatePayload(
      creditAccountId,
      creditLimitCents,
      customerData,
      currency
    );
  }

  /**
   * Execute invoice preview and credit limit sync API calls in parallel
   * @private
   * @param {number} saleId
   * @param {number} invoiceIntentAmount
   * @param {Object} payments
   * @param {Array} depositPayments
   * @param {Array} invoicesToPay
   * @param {{shouldUpdate: boolean, creditLimitCents: number|null, creditAccountId: number}} creditLimitData
   * @returns {Promise<Response[]>} Array of fetch responses
   */
  async _executeInvoicePreviewApiCalls(
    saleId,
    invoiceIntentAmount,
    payments,
    depositPayments,
    invoicesToPay,
    creditLimitData
  ) {
    const promises = [
      // Always fetch invoice preview data
      fetch(`/admin/invoicing/invoice-preview-data/${saleId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN':
            document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        },
        body: JSON.stringify({
          payments,
          depositPayments,
          invoicePayments: invoicesToPay,
          giftCardPayments: this._formatGiftCardPayments(),
          depositToAdd: this._saleManager.deposits.depositTotal.toNumber(),
          invoiceIntentAmount,
        }),
      }),
    ];

    // Conditionally add credit limit sync if customer has credit account
    if (creditLimitData.shouldUpdate) {
      const creditAccountRef = `ls-retail_${creditLimitData.creditAccountId}`;

      // Create promise that handles 404 by creating credit account
      const creditLimitPromise = fetch(
        `/admin/invoicing/v1/credit-accounts/${creditAccountRef}/credit-limit`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN':
              document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
          },
          body: JSON.stringify({ creditLimit: creditLimitData.creditLimitCents }),
        }
      ).then(async (response) => {
        // If successful, return response as-is
        if (response.ok) {
          return response;
        }

        // Check if this is a credit account 404 error
        if (await this._isCreditAccount404Error(response)) {
          // Credit account doesn't exist - create it using CreditAccountService
          if (!this._saleManager.customer) {
            throw new Error('Customer data is required to create credit account');
          }

          const customerData = {
            customerID: this._saleManager.customer.id,
            firstName: this._saleManager.customer.get('firstName'),
            lastName: this._saleManager.customer.get('lastName'),
            company: this._saleManager.customer.get('company'),
            title: this._saleManager.customer.get('title'),
            Contact: this._saleManager.customer.has('Contact')
              ? this._saleManager.customer.get('Contact')
              : undefined,
          };
          const currency = window.merchantos?.locale?.currency_code || 'USD';

          await CreditAccountService.createCreditAccount(
            `${creditLimitData.creditAccountId}`,
            creditLimitData.creditLimitCents,
            customerData,
            currency
          );

          // Return a mock successful response to indicate credit account was created
          return { ok: true, status: 201 };
        }

        // Other errors - throw to be handled by caller
        throw new Error(`Credit limit update failed: ${response.statusText}`);
      });

      promises.push(creditLimitPromise);
    }

    return Promise.all(promises);
  }

  /**
   * Validate HTTP responses and extract invoice data from API responses
   * @private
   * @param {Response[]} responses - Array of fetch responses
   * @param {{shouldUpdate: boolean}} creditLimitData - Credit limit metadata
   * @returns {Promise<Object>} Invoice data object
   * @throws {Error} If responses are invalid or data is missing
   */
  async _validateAndExtractInvoiceData(responses, creditLimitData) {
    // Validate invoice preview response (always first in array)
    const invoicePreviewResponse = responses[0];
    if (!invoicePreviewResponse.ok) {
      const errorData = await invoicePreviewResponse
        .json()
        .catch(() => ({ error: 'Unknown error' }));
      throw new Error(
        errorData.message || errorData.error || `Server error: ${invoicePreviewResponse.status}`
      );
    }

    // Validate credit limit response if it was sent
    if (creditLimitData.shouldUpdate) {
      const creditLimitResponse = responses[1];
      // Accept both 200 (updated) and 201 (created) as success
      if (!creditLimitResponse.ok && creditLimitResponse.status !== 201) {
        const errorData = await creditLimitResponse
          .json()
          .catch(() => ({ error: 'Unknown error' }));
        throw new Error(
          `Failed to update or create credit account: ${
            errorData.message || errorData.error || `Server error: ${creditLimitResponse.status}`
          }`
        );
      }
    }

    // Parse and validate invoice data
    const data = await invoicePreviewResponse.json();

    if (!data.invoiceData) {
      throw new Error('Missing required invoice preview data from API');
    }

    return data.invoiceData;
  }

  async onPreviewInvoice() {
    if (!Modules.hasModule('customers.invoicing_integration.free_tier')) {
      return;
    }
    const openInvoiceModalStartTime = new Date(Date.now()).getTime();

    const { depositPayments } = getDepositPaymentsPayload(this._saleManager.depositPaymentOptions);
    const { invoicesToPay } = getInvoicePaymentsPayload(this._saleManager.invoicePaymentOptions);
    const pendingPayments = this._saleManager.pendingPayments;
    const salePayments = getSalePaymentsForInvoice(
      getProcessedPaymentsFromSale(this._saleManager.sale),
      this._saleManager.pendingPayments
    );
    const { payments } = getSalePaymentsPayload(
      salePayments,
      this._saleManager.pendingChange,
      this._saleManager.cashRoundingDelta
    );
    const saleStatus = this._saleManager._canFinishSale();
    if (saleStatus.isError()) {
      window.showNotifier('warning', saleStatus.msg);
      $('#finishSaleButton').prop('disabled', false);
      return;
    }

    const saleId = this._saleManager.sale.id;

    const baseProps = {
      onCancel: () => {
        $('#finishSaleButton').prop('disabled', false);
      },
      onSubmit: (option, finalizeInvoiceStartTime) => {
        return window.merchantos.register
          .sendCompleteSaleRequest({
            invoicingOption: option,
            hasInvoicePayments: this._saleManager.hasInvoicePayments,
            hasDepositPayments: this._saleManager.hasDepositPayments,
          })
          .always(() => {
            const endTime = new Date(Date.now()).getTime();
            const duration = Math.abs(finalizeInvoiceStartTime - endTime) / 1000;
            datadogRum.addAction(LsInvoicingInvoiceFinalizationTime, {
              option,
              saleID: this._saleManager.sale.id,
              accountID: window.merchantos.session.getSystemCustomerID(),
              duration,
              numberOfPayments: Object.keys(pendingPayments).length,
              numberOfDepositPayments: depositPayments.length,
            });
          });
      },
      onError: (message) => {
        window.showNotifier('warning', message);
        $('#finishSaleButton').prop('disabled', false);
      },
      transactionId: saleId,
      payments,
      depositPayments,
      invoicePayments: invoicesToPay,
      depositToAdd: this._saleManager.deposits.depositTotal,
      giftCardPayments: this._saleManager.getPaymentsOfType(PaymentType.Types.GIFT_CARD).toArray(),
      openInvoiceModalStartTime,
      customerCreditAccountId: this._saleManager.creditAccountID ?? 0,
    };

    const hasDepositPayments = depositPayments && depositPayments.length > 0;
    const hasInvoicePayments = invoicesToPay && invoicesToPay.length > 0;
    // Fall back to legacy when required modules aren't enabled for deposit/invoice payment flows
    const canHandleDeposits =
      !hasDepositPayments || Modules.hasModule('invoicing.api.v1.consume_deposit');
    const canHandleInvoicePayments =
      !hasInvoicePayments || Modules.hasModule('invoicing.api.v1.payment_instructions');

    if (Modules.hasModule('invoicing.api.v1') && canHandleDeposits && canHandleInvoicePayments) {
      try {
        const pendingCreditAccountAmount = GetPendingCreditAccountPaymentAmount(pendingPayments);

        // Step 1: Refresh sale data from R-Series
        await this._refreshAndValidateSale();

        // Step 2: Extract and validate credit limit data
        const creditLimitData = this._extractCreditLimitData();

        // Step 3: Execute API calls in parallel (invoice preview + credit limit sync)
        const responses = await this._executeInvoicePreviewApiCalls(
          saleId,
          pendingCreditAccountAmount,
          payments,
          depositPayments,
          invoicesToPay,
          creditLimitData
        );

        // Step 4: Validate responses and extract invoice data
        const invoiceData = await this._validateAndExtractInvoiceData(responses, creditLimitData);

        // Step 5: Log success metric
        logInvoiceCreationMetric({
          actionName: LsInvoicingInvoiceCreationTime,
          saleID: saleId,
          startTime: openInvoiceModalStartTime,
          numberOfPayments: Object.keys(payments).length,
          numberOfDepositPayments: depositPayments.length,
          numberOfItems: getSaleLineCount(this._saleManager.sale),
          apiVersion: 'v1',
        });

        // Step 6: Render modal with preview data
        renderInvoicingModal({
          ...baseProps,
          invoiceData,
          documentPreviewURL: getProposalPreviewURL(),
        });
      } catch (error) {
        // Block sale entirely - strict fail-fast for data integrity
        Monitoring.addError(
          new Error('Failed to prepare invoice for v1 flow'),
          'PaymentController',
          {
            saleId,
            reason: error,
          }
        );

        window.showNotifier(
          'error',
          'Unable to preview invoice. Please try again or contact support.'
        );

        // Re-enable finish button so user can retry
        $('#finishSaleButton').prop('disabled', false);
      }
    } else {
      renderInvoicingModal({
        ...baseProps,
        defaultToLegacy: true,
      });
    }
  }

  /**
   * Adds a deposit of the amount specified to the sale.
   *
   * @param {Decimal} amount      The amount of the deposit.
   * @private
   */
  _doAddDeposit(amount, isAccountPayment) {
    let paymentAmount = Format.parseDecimal(amount, 0);

    if (!paymentAmount.isZero()) {
      let deposit = this._createDeposit(paymentAmount);

      if (deposit) {
        this._saleManager
          .addDeposit(deposit, isAccountPayment)
          .done(() => {
            window.flashNotifier(
              'success',
              Localize.translate(
                `Credit Account Deposit Of <var>${Format.formatMoney(amount)}</var> Added.`
              ),
              4
            );
          })
          .fail(() => {
            alertUser(Localize.translate('Could not add the deposit.'));
          });
      }
    }
  }

  /**
   * Creates a deposit.  For types (such as cash) where no additional interaction is required, the payment
   * is simply created and returned.  When additional interaction is required (e.g. credit cards) the
   * appropriate UI will be presented in order to obtain the deposit payment.
   *
   *  @param {Decimal} amount -  to be deposited.
   *  @param {PaymentType} paymentType - They type of deposit payment (used for refunds)
   */
  _createDeposit(amount, paymentType = null, isRefund = false) {
    let paymentAmount = Format.parseDecimal(amount, 0);
    let deposit = null;

    if (paymentType) {
      switch (paymentType.get('type')) {
        case PaymentType.Types.CASH:
          deposit = PaymentFactory.createPayment(
            PaymentTypeManager.Types.DEPOSIT,
            PaymentFactory.createPayment(PaymentTypeManager.Types.CASH, paymentAmount),
            this._saleManager.creditAccountID
          );
          break;

        case PaymentType.Types.CREDIT_CARD:
          if (isRefund) {
            deposit = PaymentFactory.createPayment(
              PaymentTypeManager.Types.DEPOSIT,
              PaymentFactory.createPayment(PaymentTypeManager.Types.TERMINAL, paymentAmount),
              this._saleManager.creditAccountID
            );
          } else {
            this._paymentViewController.startCreditCardDeposit(paymentAmount);
          }
          break;

        case PaymentType.Types.GIFT_CARD:
          if (!isRefund) {
            this._paymentViewController.startGiftCardDeposit(paymentAmount);
          } else {
            console.error('Gift card deposit refunds are not supported');
          }
          break;

        case PaymentType.Types.USER_DEFINED:
          deposit = PaymentFactory.createPayment(
            PaymentTypeManager.Types.DEPOSIT,
            PaymentFactory.createPayment(
              PaymentTypeManager.Types.USER_DEFINED,
              paymentAmount,
              paymentType.id
            ),
            this._saleManager.creditAccountID
          );
          break;

        default:
          break;
      }
    } else {
      // just create a straight-up deposit (credit).  The user will need to balance
      // that out with an actual payment at some point.  We use a pending payment as a
      // placeholder but it's essentially meaningless since that would never be sent
      // to the server.  It's used only to determine the amount of the deposit.
      deposit = PaymentFactory.createPayment(
        PaymentTypeManager.Types.DEPOSIT,
        PaymentFactory.createPayment(PaymentTypeManager.Types.PENDING, paymentAmount),
        this._saleManager.creditAccountID
      );
    }

    return deposit;
  }

  /**
   * Enables any controls that are rendered by the Legacy UI that trigger actions in the new code.
   * This is done to prevent undesirable affects while the application is initializing.
   */
  enableLegacyControls() {
    $('#makePaymentButton').prop('disabled', false);
    $('#addDepositButton').prop('disabled', false);
    const $refundDepositButton = $('#refundDepositButton');
    if ($refundDepositButton) {
      $refundDepositButton.prop('disabled', false);
      if ($refundDepositButton.data('trigger-on-enabled')) {
        $refundDepositButton.trigger('click');
      }
    }
  }

  /**
   *  Create and show the individual payment views this controller manages.
   */
  showPayments() {
    let creditAccountOperation = $.Deferred().resolve();
    // If this is a refund operation, and the customer is attached but does not have a creditAccountID
    // attach a creditAccountID for this customer.
    if (
      this._saleManager.isRefundSale() &&
      this._saleManager.customer &&
      !this._saleManager.creditAccountID
    ) {
      let creditAccount = new CreditAccount({
        name: this._saleManager.customer.fullName,
        customerID: this._saleManager.customer.id,
      });

      // we have to create a customer credit account through the customer
      // endpoint using a relation which restricts what can be set when doing
      // so so we need to clean out those restricted parameters.
      creditAccount.unset('archived');
      creditAccount.unset('description');
      creditAccount.unset('giftCard');
      creditAccount.unset('balance');

      this._saleManager.customer.setRelation('CreditAccount', creditAccount);
      creditAccountOperation = $.Deferred();
      this._saleManager.customer
        .save()
        .fail((jqXHR) => {
          console.log(
            'Unable to create a credit account for the customer.  Ensure that the customer has a name or address assigned.'
          );
        })
        .always(() => {
          // resolve even if it fails but we do show an error
          creditAccountOperation.resolve();
        });
    }

    creditAccountOperation
      .done(() => {
        const availableCreditForLayawayOrSO =
          SaleCalculationHelper.getAvailableCreditForLayawayOrSO(this._saleManager);

        if (
          this._saleManager.creditAccountID &&
          availableCreditForLayawayOrSO.isGreaterThan(0) &&
          !this._saleManager.deposits.hasDeposits &&
          !Modules.hasModule('customers.invoicing_integration.free_tier')
        ) {
          // Adds a credit account payment to cover layaways and deposits in the sale.  This has the affect
          // of auto-filling the credit account field in the default view.  To be consistent with the old-UI,
          // the payment will always auto-fill whenever the payment block is shown.
          let payment = PaymentFactory.createPayment(
            PaymentTypeManager.Types.CREDIT_ACCOUNT,
            availableCreditForLayawayOrSO
          );
          this._saleManager.addPayment(payment);
        }

        this._paymentViewController = new PaymentViewController(
          this._saleManager,
          this._paymentProvider
        );
        this._paymentViewController.show();

        this._paymentTotalViewController = new PaymentTotalViewController(this._saleManager);
        this._paymentTotalViewController.show();
      })
      .fail((jqXHR) => {
        // the legacy server UI holds onto the payments state so we need to go through
        // legacy functions to 'cancel' payments
        window.merchantos.register.continueSale().always(() => {
          PaymentServiceAlert.alert(jqXHR);
        });
      });
  }

  /**
   *  Destroy the individual payment views this controller manages if they exist.
   */
  closePayments() {
    if (this._paymentViewController) {
      this._paymentViewController.destroy();
      this._paymentViewController = null;
    }

    if (this._paymentTotalViewController) {
      this._paymentTotalViewController.destroy();
      this._paymentTotalViewController = null;
    }
  }

  addInvoicesToPayTile() {
    this.invoicesToPayTile = renderPaymentTile({
      type: PaymentTileType.INVOICE_PAYMENT_TILE,
      amount: this._saleManager.totalInvoicePaymentAmount,
      onRemove: async () => {
        if (this.invoicesToPayTile) {
          // Delete invoice payment instruction from Invoicing API (blocking)
          if (Modules.hasModule('invoicing.api.v1.payment_instructions')) {
            try {
              await InvoicePaymentInstructionService.deleteInvoicePaymentInstruction(
                this._saleManager.creditAccountID,
                this._saleManager.sale.id
              );
            } catch (error) {
              datadogRum.addError(error, {
                creditAccountId: this._saleManager.creditAccountID,
                saleId: this._saleManager.sale.id,
                message: 'Failed to delete invoice payment instruction',
              });
              window.showNotifier('warning', 'Failed to remove invoice payment instruction');
              return;
            }
          }

          this.invoicesToPayTile.unmount();
          // the MFE checks for a defaultValue to populate the form in the modal,
          // invoicePaymentOptions can either be undefined or { invoicesToPay: [] }
          this._saleManager.invoicePaymentOptions = undefined;
        }
      },
      onEdit: () => {
        window.merchantos.register.ensureAppIsDefined();
        window.App.channel.vent.trigger(window.App.globalEvents.register.ADD_INVOICE_PAYMENT);
      },
    });
  }

  addDepositToPayTile() {
    this.depositToPayTile = renderPaymentTile({
      type: PaymentTileType.DEPOSIT_PAYMENT_TILE,
      amount: this._saleManager.deposits.depositTotal,
      onRemove: () => {
        if (this.depositToPayTile) {
          this.depositToPayTile.unmount();
          this._saleManager.removeDeposits();
        }
      },
      onEdit: () => {
        window.merchantos.register.ensureAppIsDefined();
        window.App.channel.vent.trigger(window.App.globalEvents.register.ADD_INVOICING_DEPOSIT);
      },
    });
  }

  /**
   * Format gift card payments for invoice preview API
   * Transforms Backbone models to flat DTO structure with saleLineId and amount
   * @private
   * @returns {Array<{saleLineId: number, amount: string}>}
   */
  _formatGiftCardPayments() {
    return this._saleManager
      .getPaymentsOfType(PaymentType.Types.GIFT_CARD)
      .toArray()
      .map((payment) => ({
        saleLineId: payment.getSaleLineID(),
        amount: payment.get('amount'),
      }));
  }

  /**
   * Calls the refund intent API when a deposit refund is added.
   * This method is called BEFORE the deposit refund is added to R-Series.
   *
   * @param {Decimal} newDepositAmount The amount of the deposit refund being added
   * @returns {Promise<boolean>} True if the refund intent was successfully created/updated, false otherwise
   * @private
   */
  async _upsertRefundIntent(newDepositAmount) {
    if (!Modules.hasModule('invoicing.api.v1.refund_intent')) {
      return true; // If module is disabled, don't block the flow
    }

    const customer = this._saleManager.customer;
    const creditAccountId = customer?.get('creditAccountID');
    const customerId = customer?.id;
    const saleId = this._saleManager.sale?.id;
    const registerID = this._saleManager.sale?.get('registerID');
    const employee = window.merchantos.session.getEmployee();
    // Calculate total refund amount including the new deposit being added
    const currentRefundTotal = this._saleManager.deposits.refundTotal;
    const totalRefundAmount = currentRefundTotal.plus(newDepositAmount.abs());
    const currency = window.merchantos.locale.currency_code;

    if (!saleId || totalRefundAmount.isZero()) {
      return true; // No refund to process, continue with flow
    }

    try {
      // Get location data from R-Series
      const shopData = await InvoiceService.getLocationData();
      if (!shopData) {
        datadogRum.addError(new Error('Location data not available'), {
          message: 'Failed to get location data for refund intent',
          saleId,
        });
        return false;
      }

      await RefundIntentService.upsertRefundIntent({
        creditAccountId: creditAccountId || 0,
        customerId: `${customerId}`,
        saleId: `${saleId}`,
        amount: totalRefundAmount.toNumber(),
        currency,
        accountId: String(window.merchantos.session.getSystemCustomerID()),
        registerRef: `${registerID}`,
        invoice: {
          customer: buildCustomerFromData(customer?.attributes),
          employee: buildEmployeeFromData(
            employee
              ? {
                  employeeID: employee.employeeID,
                  firstName: employee.firstName,
                  lastName: employee.lastName,
                  email: employee?.email,
                }
              : undefined
          ),
          location: buildLocationFromShopData(shopData),
        },
        customerData: customer?.attributes,
      });
      return true;
    } catch (error) {
      // Log error and block the deposit refund flow
      datadogRum.addError(error, {
        message: 'Failed to upsert refund intent during deposit refund',
        saleId,
        creditAccountId,
        amount: totalRefundAmount.toString(),
      });
      return false;
    }
  }
}
